import React, { useState, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { LaoLetter } from '../data/letters';
import { LaoNumber } from '../data/numbers';

interface PuzzleGameProps {
  item: LaoLetter | LaoNumber;
  onSuccess: () => void;
  onBack: () => void;
  speak: (text: string) => void;
  playEffect: (type: 'correct' | 'wrong' | 'success' | 'click') => void;
}

function shuffle<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// All Lao consonants + common vowels/tones for distractors
const ALL_LAO_CHARS = [
  'ກ','ຂ','ຄ','ງ','ຈ','ສ','ຊ','ຍ','ດ','ຕ','ຖ','ທ','ນ','ບ','ປ',
  'ຜ','ຝ','ພ','ຟ','ມ','ຢ','ຣ','ລ','ວ','ຫ','ອ','ຮ',
  'ະ','າ','ິ','ີ','ຶ','ື','ຸ','ູ','ເ','ແ','ໂ','ໃ','ໄ',
  '່','້','໊','໋','ັ','ົ','ຳ'
];

interface ChoiceItem {
  char: string;
  isCorrect: boolean;
  originalIndex: number; // index in the answer array (-1 for distractors)
}

const PuzzleGame: React.FC<PuzzleGameProps> = ({ item, onSuccess, onBack, speak, playEffect }) => {
  const isLetter = 'char' in item;
  const char = isLetter ? (item as LaoLetter).char : (item as LaoNumber).laoChar;
  const name = item.name;
  const emoji = item.emoji;
  const answer = item.answer;

  // Build choices: correct answers + distractors, shuffled
  const choices = useMemo(() => {
    const correctChoices: ChoiceItem[] = answer.map((c, i) => ({
      char: c,
      isCorrect: true,
      originalIndex: i,
    }));

    // Pick distractors that aren't in the answer
    const answerSet = new Set(answer);
    const available = ALL_LAO_CHARS.filter(c => !answerSet.has(c));
    const numDistractors = Math.max(2, Math.min(4, 6 - answer.length));
    const distractors: ChoiceItem[] = shuffle(available)
      .slice(0, numDistractors)
      .map(c => ({ char: c, isCorrect: false, originalIndex: -1 }));

    return shuffle([...correctChoices, ...distractors]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [char]);

  const [slots, setSlots] = useState<(string | null)[]>(
    () => new Array(answer.length).fill(null)
  );
  const [usedIndices, setUsedIndices] = useState<Set<number>>(() => new Set());
  const [result, setResult] = useState<'none' | 'correct' | 'wrong'>('none');
  const [shakingChoices, setShakingChoices] = useState<Set<number>>(() => new Set());
  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cachedRects = useRef<DOMRect[]>([]);

  const cacheSlotRects = useCallback(() => {
    cachedRects.current = slotRefs.current.map(el =>
      el ? el.getBoundingClientRect() : new DOMRect()
    );
  }, []);

  const findSlotAtPoint = useCallback((x: number, y: number): number | null => {
    for (let i = 0; i < cachedRects.current.length; i++) {
      const rect = cachedRects.current[i];
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return i;
      }
    }
    return null;
  }, []);

  const highlightSlot = useCallback((index: number | null) => {
    slotRefs.current.forEach((el, i) => {
      if (!el || slots[i] !== null) return;
      if (i === index) {
        el.style.borderColor = '#60a5fa';
        el.style.backgroundColor = '#eff6ff';
        el.style.transform = 'scale(1.1)';
      } else {
        el.style.borderColor = '#d1d5db';
        el.style.backgroundColor = '#ffffff';
        el.style.transform = 'scale(1)';
      }
    });
  }, [slots]);

  const placeInSlot = useCallback((choice: ChoiceItem, choiceIndex: number, slotIndex: number): boolean => {
    if (slots[slotIndex] !== null) return false;

    // Check: does this character belong in this slot?
    const expectedChar = answer[slotIndex];
    if (choice.char !== expectedChar) {
      // Wrong character — reject it!
      playEffect('wrong');
      return false;
    }

    speak(choice.char);

    const newSlots = [...slots];
    newSlots[slotIndex] = choice.char;
    setSlots(newSlots);

    const newUsed = new Set(usedIndices);
    newUsed.add(choiceIndex);
    setUsedIndices(newUsed);

    // Check if all filled
    if (newSlots.every(s => s !== null)) {
      setResult('correct');
      playEffect('correct');
      setTimeout(() => onSuccess(), 1200);
    }
    return true;
  }, [slots, usedIndices, answer, speak, playEffect, onSuccess]);

  const handleDragStart = useCallback(() => {
    cacheSlotRects();
  }, [cacheSlotRects]);

  const handleDrag = useCallback((_: any, info: PanInfo) => {
    const slotIdx = findSlotAtPoint(info.point.x, info.point.y);
    highlightSlot(slotIdx);
  }, [findSlotAtPoint, highlightSlot]);

  const handleDragEnd = useCallback((choice: ChoiceItem, choiceIndex: number, _: any, info: PanInfo) => {
    highlightSlot(null);
    if (usedIndices.has(choiceIndex) || result !== 'none') return;

    const slotIdx = findSlotAtPoint(info.point.x, info.point.y);
    if (slotIdx !== null && slots[slotIdx] === null) {
      const placed = placeInSlot(choice, choiceIndex, slotIdx);
      if (!placed) {
        // Wrong letter dropped — trigger shake
        setShakingChoices(prev => new Set(prev).add(choiceIndex));
        setTimeout(() => {
          setShakingChoices(prev => {
            const next = new Set(prev);
            next.delete(choiceIndex);
            return next;
          });
        }, 600);
      }
    }
  }, [findSlotAtPoint, slots, usedIndices, result, placeInSlot, highlightSlot]);

  const handleBackspace = useCallback(() => {
    if (result !== 'none') return;
    const lastFilledIndex = slots.reduce((last, s, i) => s !== null ? i : last, -1);
    if (lastFilledIndex === -1) return;

    const removedChar = slots[lastFilledIndex];
    const newSlots = [...slots];
    newSlots[lastFilledIndex] = null;
    setSlots(newSlots);

    const shuffledIndex = choices.findIndex(
      (c, i) => c.char === removedChar && usedIndices.has(i)
    );
    if (shuffledIndex !== -1) {
      const newUsed = new Set(usedIndices);
      newUsed.delete(shuffledIndex);
      setUsedIndices(newUsed);
    }
  }, [slots, usedIndices, choices, result]);

  const handleReset = useCallback(() => {
    setSlots(new Array(answer.length).fill(null));
    setUsedIndices(new Set());
    setResult('none');
  }, [answer.length]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-b from-yellow-100 via-white to-blue-100 flex flex-col items-center p-4"
    >
      {/* Back button */}
      <div className="w-full flex justify-start mb-4">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={onBack}
          className="bg-white rounded-full shadow-md px-5 py-3 text-xl font-bold text-gray-600"
        >
          ← ກັບຄືນ
        </motion.button>
      </div>

      {/* Emoji + Name display */}
      <motion.div
        className="flex flex-col items-center mb-8"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
      >
        <span className="text-8xl md:text-9xl">{emoji}</span>
        <span className="text-3xl md:text-4xl font-bold text-gray-700 mt-2">{name}</span>
        {'emojiCount' in item && (
          <span className="text-3xl mt-2">{(item as LaoNumber).emojiCount}</span>
        )}
      </motion.div>

      {/* Slots (drop zones) */}
      <div className="flex gap-2 md:gap-4 mb-8 flex-wrap justify-center">
        {slots.map((slot, i) => (
          <div
            key={i}
            ref={el => { slotRefs.current[i] = el; }}
            className={`w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center text-3xl md:text-4xl font-bold ${
              slot === null
                ? 'border-4 border-dashed border-gray-300 bg-white'
                : result === 'correct'
                ? 'border-4 border-green-500 bg-green-100 text-green-700'
                : result === 'wrong'
                ? 'border-4 border-red-400 bg-red-100 text-red-600'
                : 'border-4 border-blue-400 bg-blue-50 text-blue-700'
            }`}
            style={{ transition: 'transform 0.15s ease, border-color 0.15s ease, background-color 0.15s ease' }}
          >
            <AnimatePresence>
              {slot && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                >
                  {slot}
                </motion.span>
              )}
            </AnimatePresence>
            {result === 'correct' && slot && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute text-green-500 text-sm"
                style={{ marginTop: '-2rem' }}
              >
                ✓
              </motion.span>
            )}
          </div>
        ))}
      </div>

      {/* Drag hint */}
      {result === 'none' && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-lg text-gray-400 mb-4"
        >
          👆 ລາກໂຕໜັງສືໃສ່ຊ່ອງຂ້າງເທິງ
        </motion.p>
      )}

      {/* Result message */}
      <AnimatePresence>
        {result === 'correct' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className="text-4xl font-bold text-green-500 mb-4"
          >
            🎉 ເກັ່ງ!
          </motion.div>
        )}
        {result === 'wrong' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className="text-3xl font-bold text-orange-500 mb-4"
          >
            ລອງໃໝ່ເດີ້! 💪
          </motion.div>
        )}
      </AnimatePresence>

      {/* Draggable Choices */}
      <div className="flex gap-3 md:gap-4 mb-6 flex-wrap justify-center">
        {choices.map((choice, i) => {
          const isShaking = shakingChoices.has(i);
          const isUsed = usedIndices.has(i);
          return (
            <motion.div
              key={`${choice.char}-${i}`}
              drag={!isUsed && result === 'none'}
              dragSnapToOrigin
              dragElastic={choice.isCorrect ? 0 : 0.7}
              dragMomentum={false}
              dragTransition={
                choice.isCorrect
                  ? { bounceStiffness: 300, bounceDamping: 20 }
                  : { bounceStiffness: 600, bounceDamping: 15 }
              }
              onDragStart={handleDragStart}
              onDrag={handleDrag}
              onDragEnd={(e, info) => handleDragEnd(choice, i, e, info)}
              whileDrag={{
                scale: choice.isCorrect ? 1.15 : 1.05,
                zIndex: 50,
                boxShadow: choice.isCorrect
                  ? '0 10px 30px rgba(0,0,0,0.2)'
                  : '0 4px 12px rgba(255,0,0,0.3)',
                rotate: choice.isCorrect ? 0 : [0, -3, 3, -3, 0],
              }}
              animate={
                isShaking
                  ? { x: [0, -8, 8, -8, 8, 0], rotate: [0, -5, 5, -5, 5, 0] }
                  : { x: 0, rotate: 0 }
              }
              transition={isShaking ? { duration: 0.4 } : undefined}
              className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl text-3xl md:text-4xl font-bold shadow-lg flex items-center justify-center select-none will-change-transform ${
                isUsed
                  ? 'bg-gray-200 text-gray-400 opacity-40'
                  : choice.isCorrect
                  ? 'bg-white text-gray-700 cursor-grab active:cursor-grabbing'
                  : 'bg-white text-gray-500 cursor-grab active:cursor-grabbing border-2 border-gray-100'
              }`}
              style={{ touchAction: 'none' }}
            >
              {choice.char}
            </motion.div>
          );
        })}
      </div>

      {/* Action buttons */}
      {/* <div className="flex gap-4">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={handleBackspace}
          className="bg-yellow-400 text-white rounded-full px-6 py-3 text-xl font-bold shadow-md min-w-[48px] min-h-[48px]"
        >
          ⌫ ລຶບ
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={handleReset}
          className="bg-gray-400 text-white rounded-full px-6 py-3 text-xl font-bold shadow-md min-w-[48px] min-h-[48px]"
        >
          🔄 ລອງໃໝ່
        </motion.button>
      </div> */}
    </motion.div>
  );
};

export default PuzzleGame;
